import React, {useEffect, useRef, useState} from 'react'
import {
    Autocomplete, Box, Button, Checkbox, Chip, Divider, FormControl, FormControlLabel, Grid,
    IconButton, InputLabel, MenuItem, Paper, Select, TextField, Tooltip
} from '@mui/material'
import Typography from '@mui/material/Typography'
import SaveIcon from '@mui/icons-material/Save'
import UploadIcon from '@mui/icons-material/Upload'
import DeleteIcon from '@mui/icons-material/Delete'
import {LoaderFunctionArgs, useLoaderData, useNavigate, useParams} from 'react-router-dom'
import {useSession} from './contexts/SessionContext'
import axios from 'axios'
import {MagicItem} from './MagicItemList'

interface Ritual {
    id: number
    name: string
    aspect: string
    extra_text: boolean
    can_target_any: boolean
    difficulty_cost_formula?: string | null
    reagent_cost_formula?: string | null
    max_per_item?: number | null
    eligible_items?: Array<{id: number; itemTypeId: number; itemType: {id: number; name: string}}>
}

interface Flaw {
    id: number
    name: string
    text: string
}

interface ItemType {
    id: number
    name: string
    is_item: boolean
}

interface EventOption {
    id: number
    name: string
    locked: boolean
}

interface AttachedRitual {
    id?: number
    ritualId: number
    ritual: Ritual
    notes: string
}

interface AttachedFlaw {
    id?: number
    flawId: number
    flaw: Flaw
}

export const magicItemLoader = async ({params}: LoaderFunctionArgs): Promise<MagicItem | null> => {
    if (!params.id) return null
    return axios.get(`/magic-items/magic-items/${params.id}`).then(({data}) => data)
}

const EMPTY_ITEM: MagicItem = {
    id: 0,
    item_id: '',
    rep_number: '',
    name: '',
    description: '',
    image_url: null,
    typeId: null,
    item_type: null,
    tp_value: null,
    note: '',
    duration: null,
    vessel_of_transference: false,
    eventId: null,
    dropped_at: null,
    rituals: [],
    flaws: [],
}

export function MagicItemViewer() {
    const params = useParams()
    const loaded = useLoaderData() as MagicItem | null
    const isNew = params.id === undefined
    const navigate = useNavigate()
    const {roles} = useSession()
    const showCma = ['GM', 'Owner'].some(r => roles.includes(r))

    const [item, setItem] = useState<MagicItem>(loaded ?? EMPTY_ITEM)
    const [attachedRituals, setAttachedRituals] = useState<AttachedRitual[]>(
        (loaded?.rituals ?? []).map(r => ({id: r.id, ritualId: r.ritualId, ritual: r.ritual, notes: r.notes ?? ''}))
    )
    const [attachedFlaws, setAttachedFlaws] = useState<AttachedFlaw[]>(
        (loaded?.flaws ?? []).map(f => ({id: f.id, flawId: f.flawId, flaw: f.flaw}))
    )

    const [ritualCatalog, setRitualCatalog] = useState<Ritual[]>([])
    const [flawCatalog, setFlawCatalog] = useState<Flaw[]>([])
    const [typeCatalog, setTypeCatalog] = useState<ItemType[]>([])
    const [eventCatalog, setEventCatalog] = useState<EventOption[]>([])

    const [selectedRitual, setSelectedRitual] = useState<Ritual | null>(null)
    const [selectedFlaw, setSelectedFlaw] = useState<Flaw | null>(null)

    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [imageUploading, setImageUploading] = useState(false)
    const [chapterNumber, setChapterNumber] = useState<number | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        axios.get('/magic-items/catalog/rituals').then(({data}) => setRitualCatalog(data)).catch(() => {})
        axios.get('/magic-items/catalog/flaws').then(({data}) => setFlawCatalog(data)).catch(() => {})
        axios.get('/magic-items/catalog/types').then(({data}) => setTypeCatalog(data)).catch(() => {})
        axios.get('/treasure/events').then(({data}) => setEventCatalog(data)).catch(() => {})
        axios.get('/admin/chapter').then(({data}) => { if (data?.chapterId) setChapterNumber(data.chapterId) }).catch(() => {})
    }, [])

    function setField(field: keyof MagicItem, value: any) {
        setItem(prev => ({...prev, [field]: value}))
    }

    function addRitual() {
        if (!selectedRitual) return
        if (attachedRituals.length >= 20) {
            setError('Maximum of 20 rituals allowed.')
            return
        }
        const currentCount = attachedRituals.filter(r => r.ritualId === selectedRitual.id).length
        if (selectedRitual.max_per_item != null && currentCount >= selectedRitual.max_per_item) {
            setError(`"${selectedRitual.name}" can only be added ${selectedRitual.max_per_item} time${selectedRitual.max_per_item === 1 ? '' : 's'} per item.`)
            return
        }
        setAttachedRituals(prev => [...prev, {ritualId: selectedRitual.id, ritual: selectedRitual, notes: ''}])
        setSelectedRitual(null)
        setError(null)
    }

    function removeRitual(ritualId: number) {
        setAttachedRituals(prev => prev.filter(r => r.ritualId !== ritualId))
    }

    function updateRitualNotes(ritualId: number, notes: string) {
        setAttachedRituals(prev => prev.map(r => r.ritualId === ritualId ? {...r, notes} : r))
    }

    function addFlaw() {
        if (!selectedFlaw) return
        if (attachedFlaws.some(f => f.flawId === selectedFlaw.id)) return
        setAttachedFlaws(prev => [...prev, {flawId: selectedFlaw.id, flaw: selectedFlaw}])
        setSelectedFlaw(null)
    }

    function removeFlaw(flawId: number) {
        setAttachedFlaws(prev => prev.filter(f => f.flawId !== flawId))
    }

    async function save() {
        if (!item.item_id.trim()) return setError('Item ID is required.')
        if (!item.name.trim()) return setError('Name is required.')
        if (item.duration != null && (item.duration < 1 || item.duration > 100)) {
            return setError('Duration must be between 1 and 100.')
        }
        setError(null)
        setSaving(true)
        const payload = {
            ...item,
            rituals: attachedRituals.map(r => ({ritualId: r.ritualId, notes: r.notes || null})),
            flaws: attachedFlaws.map(f => ({flawId: f.flawId})),
        }
        try {
            if (isNew) {
                const {data} = await axios.post('/magic-items/magic-items', payload)
                navigate(`/magic-items/${data.id}`, {replace: true})
            } else {
                const {data} = await axios.put(`/magic-items/magic-items/${item.id}`, payload)
                setItem(data)
                setAttachedRituals((data.rituals ?? []).map((r: any) => ({
                    id: r.id, ritualId: r.ritualId, ritual: r.ritual, notes: r.notes ?? ''
                })))
                setAttachedFlaws((data.flaws ?? []).map((f: any) => ({
                    id: f.id, flawId: f.flawId, flaw: f.flaw
                })))
            }
        } catch (e: any) {
            setError(e.response?.data?.error ?? 'Save failed.')
        } finally {
            setSaving(false)
        }
    }

    async function uploadImage(file: File) {
        if (!item.id) return
        setImageUploading(true)
        const form = new FormData()
        form.append('image', file)
        try {
            const {data} = await axios.post(`/magic-items/magic-items/${item.id}/image`, form)
            setItem(prev => ({...prev, image_url: data.image_url}))
        } catch {
            setError('Image upload failed.')
        } finally {
            setImageUploading(false)
        }
    }

    const droppedEventLocked = item.eventId != null && !!eventCatalog.find(e => e.id === item.eventId)?.locked

    const availableRituals = ritualCatalog.filter(r => {
        const count = attachedRituals.filter(a => a.ritualId === r.id).length
        return r.max_per_item == null ? count === 0 : count < r.max_per_item
    })
    const availableFlaws = flawCatalog.filter(f => !attachedFlaws.some(a => a.flawId === f.id))

    return (
        <Paper elevation={2} sx={{p: 3}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 1}}>
                <Typography variant="h5" sx={{flex: 1}}>
                    {isNew ? 'New Magic Item' : item.name || 'Magic Item'}
                </Typography>
                {!isNew && showCma && chapterNumber != null && (
                    <Button variant="outlined" size="small"
                        href={`https://cma.alliancelarp.com/chapters/${chapterNumber}/items/${item.item_id}`}
                        target="_blank" rel="noopener noreferrer">
                        View in CMA
                    </Button>
                )}
            </Box>

            {error && <Typography color="error" sx={{mb: 2}}>{error}</Typography>}

            <Grid container spacing={2}>
                {/* Core fields */}
                <Grid item xs={12} sm={4}>
                    <TextField label="Item ID" fullWidth required value={item.item_id}
                        onChange={e => setField('item_id', e.target.value)}/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField label="Rep Number" fullWidth value={item.rep_number ?? ''}
                        onChange={e => setField('rep_number', e.target.value)}/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                        <InputLabel>Item Type</InputLabel>
                        <Select value={item.typeId ?? ''} label="Item Type"
                            onChange={e => setField('typeId', e.target.value === '' ? null : e.target.value)}>
                            <MenuItem value=""><em>None</em></MenuItem>
                            {typeCatalog.map(t => (
                                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField label="Name" fullWidth required value={item.name}
                        onChange={e => setField('name', e.target.value)}/>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField label="TP Value" fullWidth type="number" value={item.tp_value ?? ''}
                        inputProps={{min: 0}}
                        onChange={e => setField('tp_value', e.target.value === '' ? null : parseInt(e.target.value))}/>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField label="Duration (1–100)" fullWidth type="number" value={item.duration ?? ''}
                        inputProps={{min: 1, max: 100}}
                        onChange={e => setField('duration', e.target.value === '' ? null : parseInt(e.target.value))}/>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <FormControlLabel
                        label="Vessel of Transference"
                        control={
                            <Checkbox
                                checked={item.vessel_of_transference ?? false}
                                onChange={e => setField('vessel_of_transference', e.target.checked)}
                            />
                        }
                    />
                </Grid>
                <Grid item xs={12} sm={3}/>

                <Grid item xs={12} sm={6}>
                    <Tooltip title={droppedEventLocked ? 'Event is locked — cannot change Dropped At' : ''}>
                        <FormControl fullWidth disabled={droppedEventLocked}>
                            <InputLabel>Dropped At Event</InputLabel>
                            <Select value={item.eventId ?? ''} label="Dropped At Event"
                                onChange={e => setField('eventId', e.target.value === '' ? null : e.target.value)}>
                                <MenuItem value=""><em>None</em></MenuItem>
                                {eventCatalog.map(ev => (
                                    <MenuItem key={ev.id} value={ev.id}>{ev.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Tooltip>
                </Grid>

                <Grid item xs={12}>
                    <TextField label="Description" fullWidth multiline rows={3} value={item.description ?? ''}
                        onChange={e => setField('description', e.target.value)}/>
                </Grid>
                <Grid item xs={12}>
                    <TextField label="Note" fullWidth multiline rows={2} value={item.note ?? ''}
                        onChange={e => setField('note', e.target.value)}/>
                </Grid>

                {/* Image */}
                {!isNew && (
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>Image</Typography>
                        {item.image_url && (
                            <Box sx={{mb: 1}}>
                                <img src={item.image_url} alt="Magic Item" style={{maxHeight: 200, maxWidth: '100%', borderRadius: 4}}/>
                            </Box>
                        )}
                        <input type="file" accept="image/*" ref={fileInputRef} style={{display: 'none'}}
                            onChange={e => { const f = e.target.files?.[0]; if (f) uploadImage(f) }}/>
                        <Button variant="outlined" startIcon={<UploadIcon/>}
                            onClick={() => fileInputRef.current?.click()} disabled={imageUploading}>
                            {imageUploading ? 'Uploading…' : 'Upload Image'}
                        </Button>
                    </Grid>
                )}

                {/* Rituals */}
                <Grid item xs={12}>
                    <Divider sx={{my: 1}}/>
                    <Typography variant="h6" gutterBottom>
                        Rituals ({attachedRituals.length}/20)
                    </Typography>
                    <Box sx={{display: 'flex', gap: 1, mb: 2}}>
                        <Autocomplete
                            sx={{flex: 1}}
                            options={availableRituals}
                            getOptionLabel={r => `${r.name} (${r.aspect})`}
                            value={selectedRitual}
                            onChange={(_e, v) => setSelectedRitual(v)}
                            renderInput={params => <TextField {...params} label="Add Ritual" size="small"/>}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                        />
                        <Button variant="outlined" onClick={addRitual} disabled={!selectedRitual || attachedRituals.length >= 20}>
                            Add
                        </Button>
                    </Box>
                    {attachedRituals.map(r => (
                        <Paper key={r.ritualId} variant="outlined" sx={{p: 1.5, mb: 1}}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                <Typography variant="subtitle2" sx={{flex: 1}}>{r.ritual.name}</Typography>
                                <Chip label={r.ritual.aspect} size="small" variant="outlined"/>
                                {r.ritual.extra_text && <Chip label="Extra Text" size="small" color="info"/>}
                                {r.ritual.can_target_any && <Chip label="Any Item" size="small" color="success"/>}
                                {r.ritual.max_per_item != null && <Chip label={`Max ${r.ritual.max_per_item}`} size="small" variant="outlined"/>}
                                <Tooltip title="Remove ritual">
                                    <IconButton size="small" color="error" onClick={() => removeRitual(r.ritualId)}>
                                        <DeleteIcon fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            {(r.ritual.eligible_items ?? []).length > 0 && (
                                <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5}}>
                                    {r.ritual.eligible_items!.map(e => (
                                        <Chip key={e.itemTypeId} label={e.itemType.name} size="small" variant="outlined" color="secondary"/>
                                    ))}
                                </Box>
                            )}
                            {(r.ritual.difficulty_cost_formula || r.ritual.reagent_cost_formula) && (
                                <Box sx={{display: 'flex', gap: 2, mb: 0.5}}>
                                    {r.ritual.difficulty_cost_formula && (
                                        <Typography variant="caption" color="text.secondary">
                                            Difficulty: <code>{r.ritual.difficulty_cost_formula}</code>
                                        </Typography>
                                    )}
                                    {r.ritual.reagent_cost_formula && (
                                        <Typography variant="caption" color="text.secondary">
                                            Reagent: <code>{r.ritual.reagent_cost_formula}</code>
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            {r.ritual.extra_text && <TextField
                                label="Notes" size="small" fullWidth multiline maxRows={3}
                                value={r.notes}
                                onChange={e => updateRitualNotes(r.ritualId, e.target.value)}
                            />}
                        </Paper>
                    ))}
                </Grid>

                {/* Flaws */}
                <Grid item xs={12}>
                    <Divider sx={{my: 1}}/>
                    <Typography variant="h6" gutterBottom>Flaws ({attachedFlaws.length})</Typography>
                    <Box sx={{display: 'flex', gap: 1, mb: 2}}>
                        <Autocomplete
                            sx={{flex: 1}}
                            options={availableFlaws}
                            getOptionLabel={f => f.name}
                            value={selectedFlaw}
                            onChange={(_e, v) => setSelectedFlaw(v)}
                            renderInput={params => <TextField {...params} label="Add Flaw" size="small"/>}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                        />
                        <Button variant="outlined" onClick={addFlaw} disabled={!selectedFlaw}>
                            Add
                        </Button>
                    </Box>
                    {attachedFlaws.map(f => (
                        <Paper key={f.flawId} variant="outlined" sx={{p: 1.5, mb: 1}}>
                            <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
                                <Box sx={{flex: 1}}>
                                    <Typography variant="subtitle2">{f.flaw.name}</Typography>
                                    <Typography variant="body2" color="text.secondary">{f.flaw.text}</Typography>
                                </Box>
                                <Tooltip title="Remove flaw">
                                    <IconButton size="small" color="error" onClick={() => removeFlaw(f.flawId)}>
                                        <DeleteIcon fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Paper>
                    ))}
                </Grid>

                {/* Save */}
                <Grid item xs={12}>
                    <Button variant="contained" startIcon={<SaveIcon/>} onClick={save} disabled={saving}>
                        {saving ? 'Saving…' : isNew ? 'Create Magic Item' : 'Save Changes'}
                    </Button>
                </Grid>
            </Grid>
        </Paper>
    )
}
